import type { UnitSystem } from '@/core/Units';
import type { TerrainSurfaceState } from './TerrainSurface';

/** The five canvas types (spec §6.1). */
export type TerrainTemplateId =
  | 'flat-residential'
  | 'gentle-slope'
  | 'rolling-landscape'
  | 'natural-clearing'
  | 'blank-custom';

export const TERRAIN_TEMPLATE_IDS: readonly TerrainTemplateId[] = [
  'flat-residential',
  'gentle-slope',
  'rolling-landscape',
  'natural-clearing',
  'blank-custom',
];

/**
 * Deterministic terrain generation parameters. The same parameters must always produce the same
 * heightfield (spec §6.2). All spatial values are in **meters**.
 */
export interface TerrainGenerationParams {
  templateId: TerrainTemplateId;
  /** Integer seed for the procedural noise. */
  seed: number;
  widthMeters: number;
  lengthMeters: number;
  /** Number of quads along each axis (vertices = segments + 1). */
  segments: number;
  /** Peak-to-trough elevation influence in meters. */
  elevationAmplitude: number;
  /** Noise roughness in [0, 1]; higher = busier terrain. */
  roughness: number;
  /** Direction of consistent slope in radians (used by gentle-slope), 0 = +X downhill. */
  slopeDirection: number;
  /** Slope rise across the site in meters (used by gentle-slope). */
  slopeRiseMeters: number;
  /** North orientation in degrees clockwise from +Z. */
  northDegrees: number;
}

/**
 * A regular grid of sampled heights. Row-major, `(segments + 1)` values per axis. Heights are in
 * meters relative to the site datum (y = 0).
 */
export interface Heightfield {
  /** Vertices along X (columns). */
  readonly cols: number;
  /** Vertices along Z (rows). */
  readonly rows: number;
  readonly widthMeters: number;
  readonly lengthMeters: number;
  readonly heights: Float32Array;
}

/**
 * Canonical terrain state stored in the project document. Stores generation parameters (for
 * deterministic reproduction), optionally the baked height data, and the painted ground.
 */
export interface TerrainProjectState {
  generation: TerrainGenerationParams;
  units: UnitSystem;
  /**
   * Baked heights (row-major, cols*rows).
   *
   * Originally an optimisation — the generator reproduces them from the parameters. Once the sculpt
   * tool exists they are also the *only* record of hand-graded ground, so a file that carries them
   * is authoritative and the generator is not re-run on load.
   */
  bakedHeights?: number[];
  /** Painted ground materials, run-length encoded. Absent on a site that is all grass. */
  surface?: TerrainSurfaceState;
}

/** Sample a height at grid indices with bounds clamping. */
export function heightAt(field: Heightfield, col: number, row: number): number {
  const c = Math.min(Math.max(col, 0), field.cols - 1);
  const r = Math.min(Math.max(row, 0), field.rows - 1);
  return field.heights[r * field.cols + c] ?? 0;
}

/**
 * Bilinearly sample terrain height at a world-space (x, z) coordinate. The grid is centered on the
 * origin spanning `widthMeters`×`lengthMeters`. Coordinates outside the field are clamped to the
 * edge. Pure — used by placement and raycasting math and fully unit-testable.
 */
export function sampleHeightWorld(field: Heightfield, x: number, z: number): number {
  const u = (x / field.widthMeters + 0.5) * (field.cols - 1);
  const v = (z / field.lengthMeters + 0.5) * (field.rows - 1);
  const c0 = Math.min(Math.max(Math.floor(u), 0), field.cols - 1);
  const r0 = Math.min(Math.max(Math.floor(v), 0), field.rows - 1);
  const c1 = Math.min(c0 + 1, field.cols - 1);
  const r1 = Math.min(r0 + 1, field.rows - 1);
  const tx = Math.min(Math.max(u - c0, 0), 1);
  const tz = Math.min(Math.max(v - r0, 0), 1);
  const h00 = heightAt(field, c0, r0);
  const h10 = heightAt(field, c1, r0);
  const h01 = heightAt(field, c0, r1);
  const h11 = heightAt(field, c1, r1);
  const top = h00 + (h10 - h00) * tx;
  const bottom = h01 + (h11 - h01) * tx;
  return top + (bottom - top) * tz;
}
